import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  renderBreakdown, renderLoanNumbers, formatWeight, formatCurrency,
  type LoanBreakdown,
} from './ObLoanBreakdown';
import './LoanDataDisplay.scss';

interface JewelClosure {
  jewel: string;
  loanNumber: string;
  loanKey: string;
  closureType: string;
}

interface ObReleaseReportItem {
  serialNumber: number;
  date: string | null;
  branch: string;
  loanNumber: string;
  pledgeId: string;
  obNumber: string;
  obBankName: string;
  accountHolderName: string;
  grossWeight: number;
  netWeight: number;
  loanBreakdown: LoanBreakdown[];
  totalAmount: number;
  obPrincipal: number;
  obInterest: number;
  onlinePrincipal: number;
  offlinePrincipal: number;
  onlineInterest: number;
  offlineInterest: number;
  reducingInterest: number;
  interestRate: string;
  pledgeType: string;
  closureType: string;
  jewelClosures?: JewelClosure[];
  numberOfDays: number;
  remarks: string;
}

interface ObReleaseReportProps {
  data: ObReleaseReportItem[];
}

const formatDate = (val: string | null) => {
  if (val === null || val === undefined) return '-';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatText = (val: string) => (val != null && val.length > 0 ? val : '-');

// Jewels of the same loan can go out in different ways, so closure is listed per jewel
const renderJewelClosures = (val: string, record: ObReleaseReportItem) => {
  const entries = record.jewelClosures ?? [];
  if (entries.length === 0) return formatText(val);

  return (
    <div className="loan-breakdown">
      {entries.map((entry, index) => (
        <div className="loan-breakdown__row" key={`${entry.loanNumber}-${index}`}>
          <span className="loan-breakdown__label">
            {formatText(entry.jewel)} ({formatText(entry.loanNumber)}):
          </span>
          <span className="loan-breakdown__value">{formatText(entry.closureType)}</span>
        </div>
      ))}
    </div>
  );
};

const columns: ColumnsType<ObReleaseReportItem> = [
  {
    title: 'S.No',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    width: 60,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 120,
    className: 'date-cell',
    render: formatDate,
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Org Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
    render: renderLoanNumbers,
  },
  {
    title: 'Pledge ID',
    dataIndex: 'pledgeId',
    key: 'pledgeId',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'OB Number',
    dataIndex: 'obNumber',
    key: 'obNumber',
  },
  {
    title: 'OB Bank Name',
    dataIndex: 'obBankName',
    key: 'obBankName',
  },
  {
    title: 'A/C Holder Name',
    dataIndex: 'accountHolderName',
    key: 'accountHolderName',
  },
  {
    title: 'GW (g)',
    dataIndex: 'grossWeight',
    key: 'grossWeight',
    render: (val: number, record) =>
      renderBreakdown('grossWeight', formatWeight, val, record.loanBreakdown),
  },
  {
    title: 'NW (g)',
    dataIndex: 'netWeight',
    key: 'netWeight',
    render: (val: number, record) =>
      renderBreakdown('netWeight', formatWeight, val, record.loanBreakdown),
  },
  {
    title: 'Total Amount',
    dataIndex: 'totalAmount',
    key: 'totalAmount',
    render: (val: number, record) =>
      renderBreakdown('totalAmount', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'OB Prin',
    dataIndex: 'obPrincipal',
    key: 'obPrincipal',
    render: (val: number, record) =>
      renderBreakdown('obPrincipal', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'OB Int',
    dataIndex: 'obInterest',
    key: 'obInterest',
    render: (val: number, record) =>
      renderBreakdown('obInterest', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Online Principal',
    dataIndex: 'onlinePrincipal',
    key: 'onlinePrincipal',
    render: (val: number, record) =>
      renderBreakdown('onlinePrincipal', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Offline Principal',
    dataIndex: 'offlinePrincipal',
    key: 'offlinePrincipal',
    render: (val: number, record) =>
      renderBreakdown('offlinePrincipal', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Online Interest',
    dataIndex: 'onlineInterest',
    key: 'onlineInterest',
    render: (val: number, record) =>
      renderBreakdown('onlineInterest', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Offline Interest',
    dataIndex: 'offlineInterest',
    key: 'offlineInterest',
    render: (val: number, record) =>
      renderBreakdown('offlineInterest', formatCurrency, val, record.loanBreakdown),
  },
  {
    title: 'Reducing Interest',
    dataIndex: 'reducingInterest',
    key: 'reducingInterest',
    render: formatCurrency,
  },
  {
    title: 'Interest Rate',
    dataIndex: 'interestRate',
    key: 'interestRate',
    width: 280,
    render: formatText,
  },
  {
    title: 'Type',
    dataIndex: 'pledgeType',
    key: 'pledgeType',
    filters: [
      { text: 'New', value: 'New' },
      { text: 'Repledge', value: 'Repledge' },
    ],
    onFilter: (value, record) => record.pledgeType === String(value),
  },
  {
    title: 'Closure Type',
    dataIndex: 'closureType',
    key: 'closureType',
    render: renderJewelClosures,
    filters: [
      { text: 'Cus Rel', value: 'Cus Rel' },
      { text: 'Repledge Rel', value: 'Repledge Rel' },
      { text: 'Sales Rel', value: 'Sales Rel' },
      { text: 'Action Rel', value: 'Action Rel' },
    ],
    onFilter: (value, record) =>
      (record.closureType ?? '').split(', ').includes(String(value)),
  },
  {
    title: 'No of Days',
    dataIndex: 'numberOfDays',
    key: 'numberOfDays',
  },
  {
    title: 'Remarks',
    dataIndex: 'remarks',
    key: 'remarks',
  },
];

const ObReleaseReportDisplay: React.FC<ObReleaseReportProps> = ({ data }) => {
  // Totals cover the whole report, not just the visible page.
  const totals = useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          grossWeight: acc.grossWeight + (item.grossWeight ?? 0),
          netWeight: acc.netWeight + (item.netWeight ?? 0),
          totalAmount: acc.totalAmount + (item.totalAmount ?? 0),
          obPrincipal: acc.obPrincipal + (item.obPrincipal ?? 0),
          obInterest: acc.obInterest + (item.obInterest ?? 0),
          onlinePrincipal: acc.onlinePrincipal + (item.onlinePrincipal ?? 0),
          offlinePrincipal: acc.offlinePrincipal + (item.offlinePrincipal ?? 0),
          onlineInterest: acc.onlineInterest + (item.onlineInterest ?? 0),
          offlineInterest: acc.offlineInterest + (item.offlineInterest ?? 0),
          reducingInterest: acc.reducingInterest + (item.reducingInterest ?? 0),
        }),
        {
          grossWeight: 0,
          netWeight: 0,
          totalAmount: 0,
          obPrincipal: 0,
          obInterest: 0,
          onlinePrincipal: 0,
          offlinePrincipal: 0,
          onlineInterest: 0,
          offlineInterest: 0,
          reducingInterest: 0,
        },
      ),
    [data],
  );

  return (
    <div className="loan-data-container">
      <h2>OB Release Report</h2>
      <Table
        columns={columns}
        dataSource={data.map((item) => ({
          ...item,
          key: `${item.pledgeId}-${item.serialNumber}`,
        }))}
        rowKey="key"
        pagination={{ pageSize: 20, showTotal: (t) => `${t} records` }}
        scroll={{ x: true }}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="totals-row">
              <Table.Summary.Cell index={0} colSpan={8}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8}>
                <strong>{totals.grossWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9}>
                <strong>{totals.netWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <strong>{formatCurrency(totals.totalAmount)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11}>
                <strong>{formatCurrency(totals.obPrincipal)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={12}>
                <strong>{formatCurrency(totals.obInterest)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={13}>
                <strong>{formatCurrency(totals.onlinePrincipal)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={14}>
                <strong>{formatCurrency(totals.offlinePrincipal)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={15}>
                <strong>{formatCurrency(totals.onlineInterest)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={16}>
                <strong>{formatCurrency(totals.offlineInterest)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={17}>
                <strong>{formatCurrency(totals.reducingInterest)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={18}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={19}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={20}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={21}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={22}>-</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default ObReleaseReportDisplay;
